/**
 * Composition root for persistence (Phase 5a/5b). The single place that
 * decides which `ScenarioRepository` implementation the app uses. Everything
 * else depends on the interface, never on a concrete class.
 *
 *   - Supabase env vars present  → SupabaseRepository (durable, multi-device,
 *     per-author via auth + RLS). Phase 5b.
 *   - otherwise                  → LocalStorageRepository (durable per-browser,
 *     no backend). Phase 5a.
 *
 * `supabaseConfigured` is a build-time constant, so a build without the env
 * vars set tree-shakes the Supabase path (and @supabase/supabase-js) out of
 * the bundle entirely — the localStorage-only build stays lean.
 */
import type { ScenarioRepository } from './ScenarioRepository';
import { LocalStorageRepository } from './LocalStorageRepository';
import { supabaseConfigured, getSupabaseClient } from './supabaseClient';
import { SupabaseRepository } from './SupabaseRepository';

let instance: ScenarioRepository | null = null;

/** Get the active repository (singleton). */
export function getRepository(): ScenarioRepository {
  if (!instance) {
    instance = supabaseConfigured
      ? new SupabaseRepository(getSupabaseClient())
      : new LocalStorageRepository();
  }
  return instance;
}

/** Whether the app is running against Supabase (vs local-only). */
export function isCloudPersistence(): boolean {
  return supabaseConfigured;
}

/** Test seam: override the repository (used by smoke tests / Phase 5b wiring). */
export function setRepository(repo: ScenarioRepository): void {
  instance = repo;
}
